import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ReactPlayer from 'react-player';

interface VideoPlayerProps {
  /** URL del video (supporta qualsiasi formato supportato da ReactPlayer) */
  src: string;
  /** Callback chiamata con il tempo corrente in millisecondi */
  onProgress?: (currentTimeMs: number) => void;
  /** Callback chiamata quando la durata del video è disponibile (in millisecondi) */
  onDuration?: (durationMs: number) => void;
  /** Classe CSS opzionale per il contenitore */
  className?: string;
}

export interface VideoPlayerHandle {
  /** Sposta la riproduzione a un tempo specifico (in millisecondi) */
  seekTo(timeMs: number): void;
  /** Restituisce il tempo corrente in millisecondi */
  getCurrentTime(): number;
}

/**
 * Player video che avvolge ReactPlayer.
 * Espone funzionalità di seek e monitoraggio del progresso.
 * Non include timeline né overlay: questi sono gestiti da componenti esterni.
 */
const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(
  ({ src, onProgress, onDuration, className }, ref) => {
    const playerRef = useRef<ReactPlayer>(null);
    const [duration, setDuration] = useState<number>(0);
    const currentTimeRef = useRef<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    // Espone i metodi di controllo
    useImperativeHandle(ref, () => ({
      seekTo(timeMs: number) {
        playerRef.current?.seekTo(timeMs / 1000, 'seconds');
      },
      getCurrentTime() {
        return currentTimeRef.current;
      },
    }));

    // Callback di progresso di react-player
    const handleProgress = (state: { playedSeconds: number; played: number }) => {
      const timeMs = state.playedSeconds * 1000;
      currentTimeRef.current = timeMs;
      onProgress?.(timeMs);
    };

    // Callback quando il video è pronto
    const handleReady = () => {
      setLoading(false);
    };

    // Callback di errore
    const handleError = () => {
      setError(true);
      setLoading(false);
    };

    // Callback di durata
    const handleDuration = (dur: number) => {
      const ms = dur * 1000;
      setDuration(ms);
      onDuration?.(ms);
    };

    return (
      <div
        className={className}
        style={{
          position: 'relative',
          backgroundColor: '#000',
          borderRadius: '8px',
          overflow: 'hidden',
          width: '100%',
          maxHeight: '500px',
          aspectRatio: '16 / 9',
        }}
      >
        {loading && !error && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.7)',
              zIndex: 10,
            }}
          >
            <span>Caricamento video...</span>
          </div>
        )}
        {error && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.7)',
              zIndex: 10,
            }}
          >
            <span style={{ color: '#ef4444' }}>Errore nel caricamento del video</span>
          </div>
        )}
        <ReactPlayer
          ref={playerRef}
          url={src}
          width="100%"
          height="100%"
          playing
          controls
          onProgress={handleProgress}
          onDuration={handleDuration}
          onReady={handleReady}
          onError={handleError}
          progressInterval={100}
          config={{
            file: {
              attributes: {
                crossOrigin: 'anonymous',
              },
            },
          }}
        />
      </div>
    );
  }
);

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;